import { pubPageAdTargeting } from "@config/AdsConstants";
import CatfishAd from "@elements/Advertisement/CatfishAd";
import { GoogleAdsSlotFactory } from "@elements/Advertisement/helpers";
import PrestitialAd from "@elements/Advertisement/PrestitialAd";
import { AdsLb1 } from "@elements/Advertisement/variant/AdsLb";
import PromoteEpaper from "@elements/Banner/PromoteEpaper/PromoteEpaper";
import PromoteTM from "@elements/Banner/PromoteTM/PromoteTM";
import { Footer } from "@footer/Footer";
import { Header } from "@header/Header";
import { QUICKNEWS_FLAG } from "@helper/getEnvVariables";
import { useNeuronPageViewPush } from "@hooks/useAnalyticsCall/useNeuronPageViewPush";
import useGlobalAdSetting from "@hooks/useGlobalAdSetting/useGlobalAdSetting";
import useSendPermutiveIdentify from "@hooks/useSendPermutiveIdentify";
import { BackToQuickNews, QnFloatButton } from "@modules/QuickNews/QnButtons";
import {
  AdTargetingType,
  AdvertisementTypeEnum,
  ArticleAdSlotTargetingType,
  PageAdTargetValue,
} from "@typings/Ads.d";
import type { GADataProps, MetaTagsProps } from "@typings/PageMetaData";
import { ReactElement, ReactNode } from "react";
import { useLocation } from "react-router";

type CommonProps = Readonly<{
  testId?: string;
  children: ReactNode;
  gaData?: GADataProps;
  metaTagsProps?: MetaTagsProps;
  pageAdTargetValue: PageAdTargetValue;
  showTmBanner?: boolean;
  showEpaperBanner?: boolean;
  searchedText?: string;
  gsChannels?: string[];
  sectionName: string;
  bigHeader?: boolean;
  articleSlotTarget?: ArticleAdSlotTargetingType[];
  isArticlePage?: boolean;
}>;

type WithEpaperBanner = {
  showEpaperBanner: true;
  epaperCoverImageUrl: string;
};

type WithoutEpaperBanner = {
  showEpaperBanner?: false | undefined;
  epaperCoverImageUrl?: string;
};

export type Props = CommonProps & (WithEpaperBanner | WithoutEpaperBanner);

/*
 * Container to provide a fixed width and horizontal padding around the content. Typically paired
 * with a Layout component to create reusable templates across all pages. Width of child content as
 * follows with each screen size:

  Viewport width >= 1280: Children component width: 1140px
  Viewport width >= 1024: Children component width: 990px

 * @param {ReactElement} children - Any React Element but likely used to wrap a Tabla module
 * @param {String} testId - Unique string used to target elements when testing
 * @returns {ReactElement} - A wrapped module with a fixed width that resizes on view port resize
 */

export default function MainContainer({
  children,
  testId = "main-container",
  pageAdTargetValue,
  showTmBanner = false,
  showEpaperBanner = false,
  searchedText = "",
  epaperCoverImageUrl,
  gsChannels,
  sectionName,
  bigHeader = false,
  articleSlotTarget,
  isArticlePage = false,
}: Props): ReactElement {
  const { isPrestitialEnabled, isCatfishEnabled } = useGlobalAdSetting();
  useNeuronPageViewPush();
  useSendPermutiveIdentify();
  sectionName = sectionName == "/" ? "home" : sectionName;
  const pageAdTarget = pubPageAdTargeting[pageAdTargetValue] as AdTargetingType<
    string,
    PageAdTargetValue
  >;

  // Sets quick news reference code.
  const qnRefcode =
    sectionName == "home" ? "home-quick-news" : "listing-quick-news";

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const refCode = searchParams.get("ref");
  const isQuickNews = refCode === "quick-news";
  const showHeader = !isQuickNews;

  return (
    <main data-testid={testId} className={"w-full bg-white"}>
      {isQuickNews && isArticlePage && (
        <>
          <BackToQuickNews />
          <div>
            {/* Top header LB1 ads */}
            <div className={"header-ad bg-grey-500 xmd:relative"}>
              <AdsLb1
                sectionName={sectionName}
                pageAdTargetType={pageAdTargetValue}
                gsChannels={gsChannels}
                articleSlotTarget={articleSlotTarget}
              />
            </div>
          </div>
        </>
      )}
      {showHeader && (
        <Header
          pageAdTargetValue={pageAdTargetValue}
          className="w-full mx-auto"
          searchedText={searchedText}
          gsChannels={gsChannels}
          sectionName={sectionName}
          bigHeader={bigHeader}
          articleSlotTarget={articleSlotTarget}
        />
      )}

      {QUICKNEWS_FLAG === "1" && (
        <QnFloatButton
          refCode={qnRefcode}
          isCatfishEnabled={isCatfishEnabled}
        />
      )}
      <div className="w-full mx-auto lg:p-0">
        <PrestitialAd
          slot={GoogleAdsSlotFactory(
            AdvertisementTypeEnum.PRESTITIAL,
            sectionName,
          )}
          isPrestitialEnabled={isPrestitialEnabled}
          pubAdTargeting={[pageAdTarget]}
        />
        {children}
      </div>

      {showTmBanner && <PromoteTM className="w-full" />}

      {showEpaperBanner && (
        <PromoteEpaper
          className="w-full"
          epaperCoverImageUrl={epaperCoverImageUrl}
        />
      )}
      <Footer />
      <CatfishAd
        slot={GoogleAdsSlotFactory(AdvertisementTypeEnum.CATFISH, sectionName)}
        isCatfishEnabled={isCatfishEnabled}
        pubAdTargeting={[pageAdTarget]}
        gsChannels={gsChannels}
      />
    </main>
  );
}
